/**
 * Shared iOS IAP purchase orchestration (membership subscriptions).
 * Ensures StoreKit UI is not blocked by web overlays and sync is best-effort after Apple confirms.
 */

import axios from 'axios';
import apiClient from '@/api/client';
import type { CustomerInfo } from '@revenuecat/purchases-capacitor';
import {
  ensureRevenueCatReady,
  purchaseRcPackage,
  type IapPurchaseResult,
} from '@/services/revenueCatService';

export interface IapPrepareResult {
  skipped: boolean;
}

export async function runIapPrepare(
  url: string,
  body: Record<string, unknown>,
): Promise<IapPrepareResult> {
  try {
    const response = await apiClient.post<{ allowed?: boolean; message?: string }>(
      url,
      body,
    );
    if (response.data?.allowed === false) {
      throw new Error(response.data.message || 'Purchase is not available.');
    }
    return { skipped: false };
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      (error.response?.status === 404 || error.response?.status === 501)
    ) {
      return { skipped: true };
    }
    if (axios.isAxiosError(error) && error.response?.data) {
      const data = error.response.data as { allowed?: boolean; message?: string };
      if (data.allowed === false) {
        throw new Error(data.message || 'Purchase is not available.');
      }
    }
    throw error;
  }
}

export interface ExecuteNativeIapPurchaseParams {
  appUserId: string;
  rcPackageId: string;
  /** Called immediately before StoreKit / App Store sheet is presented. */
  onBeforeStoreKit?: () => void;
}

/**
 * Configure RevenueCat, present the App Store purchase sheet, return result.
 */
export async function executeNativeIapPurchase(
  params: ExecuteNativeIapPurchaseParams,
): Promise<IapPurchaseResult> {
  await ensureRevenueCatReady(params.appUserId);
  params.onBeforeStoreKit?.();
  return purchaseRcPackage(params.appUserId, params.rcPackageId);
}

export async function runIapSyncBestEffort(
  url: string,
  payload: Record<string, unknown>,
  customerInfo: CustomerInfo,
): Promise<void> {
  try {
    await apiClient.post(url, payload);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return;
    }
    console.warn('[IAP] Backend sync failed; entitlement may update via webhook.', error);
  }
}
